import React from 'react';
import { useSubscriptionContext } from '../contexts/SubscriptionContext';
import { Navigate } from 'react-router-dom';

/**
 * A wrapper component that hides or blocks access to children if the active subscription
 * does not have the required module enabled.
 * 
 * @param {string} module - The name of the module required (e.g., 'crm', 'woocommerce')
 * @param {boolean} hide - If true, renders nothing. If false, renders a "locked" state or redirects.
 * @param {ReactNode} children - The component(s) to render if permitted.
 * @param {ReactNode} fallback - Optional component to render if blocked (overrides hide behavior).
 */
const SubscriptionGuard = ({ module, hide = false, fallback, children }) => {
  const { hasModule, isLoading } = useSubscriptionContext();

  if (isLoading) {
    // Optionally return a spinner or null while checking subscription
    return null;
  }

  const isPermitted = hasModule(module);

  if (isPermitted) {
    return <>{children}</>;
  }

  // Not permitted
  if (fallback) {
    return <>{fallback}</>;
  }

  if (hide) {
    return null;
  }

  // Default fallback: show a message
  return (
    <div style={{ padding: '2rem', textAlign: 'center', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
      <h3 style={{ color: '#64748b', marginBottom: '0.5rem' }}>Feature Locked</h3>
      <p style={{ color: '#94a3b8' }}>Your current subscription package does not include the {module.toUpperCase()} module. Please upgrade to access this feature.</p>
    </div>
  );
};

export default SubscriptionGuard;
